<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { useProductEditorContext } from '@/composition/catalog/use-product-editor';
  import { savedSizes, sizeOption } from '@/domain/catalog/catalog-filters';
  import { stationList } from '@/domain/catalog/printing-form';
  import { popularControlsVisible } from '@/domain/catalog/popular-showcase';
  import { MAX_LONG_TEXT_LENGTH } from '@/domain/catalog/product-form';
  import { ComandaSplit } from '@/enums/ComandaSplit';
  import { ILanguage } from '@/interfaces/ILanguage';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { vueData } from '@/VueData';
  import V2CatalogEditorSection from '../shared/V2CatalogEditorSection.vue';
  import V2CatalogPopularRow from '../shared/V2CatalogPopularRow.vue';
  import V2CatalogSettingsDisclosure from '../shared/V2CatalogSettingsDisclosure.vue';
  import V2CatalogTranslationsSettings from '../shared/V2CatalogTranslationsSettings.vue';
  import V2CatalogInvoicingSettings from './V2CatalogInvoicingSettings.vue';
  import V2CatalogPrintingSettings from './V2CatalogPrintingSettings.vue';
  import V2CatalogStockSettings from './V2CatalogStockSettings.vue';

  /**
   * «Más ajustes» of the product (plan §5.12): one level of summary rows,
   * each expanding in place. Plan-gated rows show the upsell instead of
   * hiding; stock and translations load the first time they open.
   */
  const editor = useProductEditorContext();
  const { data, access } = useCatalogPage();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const form = computed(() => editor.form.value);
  const item = computed(() => editor.item.value);
  const readOnly = computed(() => editor.readOnly.value);
  const stringId = computed(() => data.restaurant.value?.stringId ?? null);

  //#region Summaries

  const printing = computed(() => {
    const current = form.value;
    if (!current) return { text: null, warn: false };
    const inherited = current.comandaPrinters === null;
    const names = stationList(
      inherited
        ? editor.category.value?.comandaPrinters ?? []
        : current.comandaPrinters,
      (id) => restaurantStore.stationName(id)
    );
    if (!names) return { text: t('menuPage.printing.none'), warn: true };
    const parts = [
      inherited
        ? t('menuPage.printing.summaryInherit', { stations: names })
        : names,
    ];
    if (current.comandaSplit !== ComandaSplit.None)
      parts.push(t('menuPage.printing.summaryApart'));
    return { text: parts.join(' · '), warn: false };
  });

  const invoicingSummary = computed(() => {
    const vat = form.value?.vatText.trim();
    if (!vat) return t('menuPage.invoicing.noVat');
    return t(
      editor.country.value === 'PE'
        ? 'menuPage.invoicing.igvSummary'
        : 'menuPage.invoicing.vatSummary',
      { n: vat }
    );
  });

  const languages = computed<ILanguage[]>(
    () => vueData.restaurantLanguages ?? []
  );
  const translationsSummary = computed(() =>
    languages.value.length
      ? languages.value.map((language) => language.name).join(', ')
      : t('menuPage.translations.noLanguagesShort')
  );
  const sizes = computed(() => (item.value ? savedSizes(item.value) : []));
  const sizeOptionId = computed(() =>
    item.value ? sizeOption(item.value)?.id ?? null : null
  );
  const hasNewSizes = computed(() => {
    const price = form.value?.price;
    return price?.mode === 'sizes' && price.rows.some((row) => row.id === null);
  });
  const showTranslations = computed(
    () =>
      access.value.canTranslate ||
      access.value.upsellTranslate ||
      access.value.readTranslations
  );

  /**
   * The Stock row's summary: amber while an assistant change of the stock
   * waits to be read again (its body says what to do; plan P5-3).
   */
  const stockStale = ref(false);
  const stockSummary = computed<{ text: string; tone: 'neutral' | 'amber' }>(
    () =>
      stockStale.value
        ? { text: t('assistant.editorStock.summary'), tone: 'amber' }
        : { text: t('menuPage.stock.summary'), tone: 'neutral' }
  );

  function onStockStale(value: boolean) {
    stockStale.value = value;
  }

  const recipeSummary = computed(() => {
    const firstLine = (form.value?.recipe ?? '')
      .split('\n')
      .map((line) => line.trim())
      .find(Boolean);
    return firstLine ?? t('menuPage.recipe.empty');
  });

  const showLabel = computed(
    () => access.value.showLabels && !!item.value?.labelTemplate
  );
  const stickersPath = computed(() =>
    stringId.value ? `/${stringId.value}/stickers` : null
  );

  /**
   * «Lo más pedido» (owner 2026-11-03): only for a saved product, while the
   * storefront shows a ranking. A category kept out keeps its products out:
   * the switch then shows off, locked, and says why.
   */
  const showPopular = computed(
    () =>
      !!item.value &&
      popularControlsVisible({
        branches: data.restaurant.value?.branches,
        showPopular: data.restaurant.value?.settings?.showPopular,
      })
  );
  const popularCategory = computed(() =>
    editor.category.value?.popularHidden ? editor.category.value : null
  );
  const popularNote = computed(() =>
    popularCategory.value
      ? t('menuPage.popular.categoryNote', {
          name: popularCategory.value.name,
        })
      : null
  );

  function setPopularShown(shown: boolean) {
    editor.update('popularHidden', !shown);
  }

  //#endregion
</script>

<template>
  <V2CatalogEditorSection
    v-if="form"
    class="v2-catalog-more-settings-section"
    :title="t('menuPage.editor.moreSettings')"
    flush
  >
    <V2CatalogSettingsDisclosure
      :title="t('menuPage.printing.title')"
      :summary="printing.text"
      :tone="printing.warn ? 'amber' : 'neutral'"
    >
      <V2CatalogPrintingSettings />
    </V2CatalogSettingsDisclosure>

    <V2CatalogSettingsDisclosure
      :title="t('menuPage.invoicing.title')"
      :summary="invoicingSummary"
    >
      <V2CatalogInvoicingSettings />
    </V2CatalogSettingsDisclosure>

    <V2CatalogSettingsDisclosure
      v-if="access.canStock || access.upsellStock"
      :title="t('menuPage.stock.title')"
      :summary="stockSummary.text"
      :tone="stockSummary.tone"
      :upsell="!access.canStock"
      :string-id="stringId"
    >
      <V2CatalogStockSettings @stale="onStockStale" />
    </V2CatalogSettingsDisclosure>

    <V2CatalogSettingsDisclosure
      v-if="showTranslations"
      :title="t('menuPage.translations.title')"
      :summary="translationsSummary"
      :upsell="!access.readTranslations"
      :string-id="stringId"
    >
      <V2CatalogTranslationsSettings
        v-if="item && data.restaurant.value"
        entity="item"
        :restaurant-id="data.restaurant.value.id"
        :source="item"
        :sizes="sizes"
        :size-option-id="sizeOptionId"
        :has-new-sizes="hasNewSizes"
        :can-edit="access.canTranslate"
        :submitted="editor.submitted.value"
        :register="editor.registerExtra"
      />
    </V2CatalogSettingsDisclosure>

    <V2CatalogSettingsDisclosure
      :title="t('menuPage.recipe.title')"
      :summary="recipeSummary"
    >
      <PosUiTextarea
        class="recipe-input"
        :value="form.recipe"
        :maxlength="MAX_LONG_TEXT_LENGTH"
        :placeholder="t('menuPage.recipe.placeholder')"
        :auto-grow="{ minRows: 3, maxRows: 10 }"
        :disabled="readOnly"
        :aria-label="t('menuPage.recipe.title')"
        @input="editor.update('recipe', $event)"
      />
    </V2CatalogSettingsDisclosure>

    <V2CatalogPopularRow
      v-if="showPopular"
      :value="!form.popularHidden && !popularCategory"
      :body="t('menuPage.popular.itemBody')"
      :note="popularNote"
      :disabled="readOnly || !!popularCategory"
      @input="setPopularShown"
    />

    <div v-if="showLabel" class="label-row">
      <span class="label-title">{{ t('menuPage.label.title') }}</span>
      <span class="label-summary">
        {{ t('menuPage.label.has') }} ·
        <router-link
          v-if="stickersPath"
          :to="stickersPath"
          class="label-link"
          >{{ t('menuPage.label.open') }}</router-link
        >
      </span>
    </div>
  </V2CatalogEditorSection>
</template>

<style scoped lang="scss">
  .v2-catalog-more-settings-section {
    .recipe-input {
      @apply tw-w-full;
    }

    .label-row {
      @apply tw-flex tw-min-h-[56px] tw-flex-col tw-justify-center tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-px-4 tw-py-2;

      .label-title {
        @apply tw-text-sm tw-font-medium tw-text-neutral-900;
      }

      .label-summary {
        @apply tw-mt-0.5 tw-text-xs tw-leading-5 tw-text-neutral-500;
      }

      .label-link {
        @apply tw-font-semibold tw-text-neutral-900 tw-underline;
      }
    }
  }
</style>
